import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { articleApi, metadataApi, briefApi, opportunityApi } from '../services/api';
import { Article, Category, DailyBrief, OpportunitySummary } from '../types';
import { TYPE_LABELS, RISK_LABELS } from '../data/opportunities';
import { ArticleCard } from '../components/ArticleCard';
import { AdSense } from '../components/AdSense';
import { FearGreedWidget } from '../components/FearGreedWidget';
import { TopMovers } from '../components/TopMovers';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';
const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';

const SHORTCUTS = [
  { to: '/resumo-diario', icon: '⚡', title: 'O Mercado em 3 Minutos', desc: 'Resumo diário editorial' },
  { to: '/ferramentas/calculadora-dca', icon: '📈', title: 'Calculadora DCA', desc: 'Simule aportes recorrentes' },
  { to: '/ferramentas/conversor', icon: '💱', title: 'Conversor', desc: 'Cripto ↔ reais em tempo real' },
  { to: '/aprenda', icon: '🎓', title: 'Aprenda Cripto', desc: 'Trilha do zero à primeira compra' },
  { to: '/ferramentas', icon: '🧰', title: 'Todas as Ferramentas', desc: 'DCA, lucro, staking, gás e mais' },
  { to: '/glossario', icon: '📚', title: 'Glossário Cripto', desc: '25+ termos em português' },
];

export const HomePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('busca') || '';

  const [articles, setArticles] = useState<Article[]>([]);
  const [trendingArticles, setTrendingArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brief, setBrief] = useState<DailyBrief | null>(null);
  const [radarOpportunities, setRadarOpportunities] = useState<OpportunitySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>();

  useEffect(() => {
    loadData();
  }, [page, selectedCategory, searchQuery]);

  useEffect(() => {
    // O brief e categorias não dependem de página/filtro
    briefApi.getLatest().then(setBrief).catch(() => setBrief(null));
    opportunityApi.getOpportunities({ limit: 5 }).then(setRadarOpportunities).catch(() => {});
    metadataApi.getCategories().then(setCategories).catch(() => {});
    articleApi.getTrendingArticles(5).then(setTrendingArticles).catch(() => {});
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const articlesRes = await articleApi.getArticles({
        page,
        limit: 13,
        categoryId: selectedCategory,
        search: searchQuery || undefined,
      });
      setArticles(articlesRes.data);
      setTotalPages(articlesRes.pagination.totalPages);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (categoryId: string | undefined) => {
    setSelectedCategory(categoryId);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchParams({});
    setPage(1);
  };

  // Destaque: primeiro artigo com imagem (apenas na visão padrão, página 1)
  const isDefaultView = !searchQuery && !selectedCategory && page === 1;
  const featured = isDefaultView ? articles.find((a) => a.imageUrl) : undefined;
  const gridArticles = featured ? articles.filter((a) => a.id !== featured.id) : articles;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description:
      'Portal de inteligência cripto em português: notícias contextualizadas, resumo diário do mercado, glossário e ferramentas.',
    inLanguage: 'pt-BR',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/?busca={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <Helmet>
        <title>{SITE_NAME} — Notícias e Inteligência Cripto em Português</title>
        <meta
          name="description"
          content="Notícias de Bitcoin, Ethereum e do mercado cripto com contexto editorial em português: resumo diário, impacto no mercado, glossário e ferramentas para o investidor brasileiro."
        />
        <link rel="canonical" href={SITE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SITE_URL} />
        <meta property="og:title" content={`${SITE_NAME} — Inteligência Cripto em Português`} />
        <meta
          property="og:description"
          content="Notícias contextualizadas, resumo diário do mercado e ferramentas cripto em português."
        />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${SITE_NAME} — Inteligência Cripto`} />
        <meta
          name="twitter:description"
          content="Notícias contextualizadas, resumo diário e ferramentas do mercado cripto em português."
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Banner do resumo diário */}
          {isDefaultView && brief && (
            <Link
              to="/resumo-diario"
              className="block bg-gradient-to-r from-blue-700 to-indigo-700 rounded-xl shadow-lg p-6 mb-8 text-white hover:from-blue-800 hover:to-indigo-800 transition"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                <div className="flex-shrink-0">
                  <p className="text-blue-200 text-xs font-bold uppercase tracking-wider">
                    ⚡ O mercado em 3 minutos
                  </p>
                  <p className="text-blue-200 text-xs capitalize">
                    {format(parseISO(brief.date), "EEEE, dd/MM", { locale: ptBR })}
                  </p>
                </div>
                <p className="font-bold text-lg leading-snug flex-grow">{brief.title}</p>
                <span className="flex-shrink-0 bg-white/15 border border-white/30 rounded-lg px-4 py-2 text-sm font-semibold">
                  Ler resumo →
                </span>
              </div>
            </Link>
          )}

          {/* Cabeçalho de busca */}
          {searchQuery && (
            <div className="mb-6 flex items-center justify-between bg-white rounded-xl shadow px-6 py-4">
              <h1 className="text-lg font-bold text-gray-900">
                Resultados para “{searchQuery}”
              </h1>
              <button onClick={clearSearch} className="text-sm text-blue-600 hover:underline">
                Limpar busca
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Category Filter */}
              <div className="mb-6 overflow-x-auto">
                <div className="flex space-x-2 pb-2">
                  <button
                    onClick={() => handleCategoryChange(undefined)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                      !selectedCategory
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Todas
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryChange(category.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                        selectedCategory === category.id
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="text-gray-500">Carregando...</div>
                </div>
              ) : articles.length === 0 ? (
                <div className="bg-white rounded-xl shadow p-8 text-center text-gray-600">
                  Nenhum artigo encontrado{searchQuery ? ` para “${searchQuery}”` : ''}.
                </div>
              ) : (
                <>
                  {/* Destaque principal */}
                  {featured && (
                    <Link
                      to={`/article/${featured.slug}`}
                      className="block bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition mb-6"
                    >
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={featured.imageUrl!}
                          alt={featured.titlePt || featured.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-6">
                        {featured.category && (
                          <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">
                            {featured.category.name}
                          </span>
                        )}
                        <h2 className="text-2xl font-bold text-gray-900 mt-1 mb-2 leading-tight">
                          {featured.titlePt || featured.title}
                        </h2>
                        {(featured.summaryPt || featured.subtitle) && (
                          <p className="text-gray-600 leading-relaxed line-clamp-3">
                            {featured.subtitle || featured.summaryPt}
                          </p>
                        )}
                        <p className="text-blue-600 text-sm font-semibold mt-3">Ler artigo completo →</p>
                      </div>
                    </Link>
                  )}

                  {/* Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {gridArticles.map((article, index) => (
                      <div key={article.id}>
                        <ArticleCard article={article} />
                        {(index + 1) % 4 === 0 && (
                          <div className="my-6">
                            <AdSense adSlot="1234567890" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-8 flex justify-center space-x-2">
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-4 py-2 bg-white rounded shadow disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        Anterior
                      </button>
                      <span className="px-4 py-2 bg-blue-600 text-white rounded shadow">
                        {page} / {totalPages}
                      </span>
                      <button
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="px-4 py-2 bg-white rounded shadow disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        Próxima
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Radar de oportunidades */}
              {isDefaultView && radarOpportunities.length > 0 && (
                <div className="mt-10">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-900">🎯 Radar de oportunidades</h2>
                    <Link to="/oportunidades" className="text-sm text-blue-600 font-semibold hover:underline">
                      Ver todas →
                    </Link>
                  </div>
                  <div className="space-y-3">
                    {radarOpportunities.map((op) => (
                      <Link
                        key={op.id}
                        to={`/oportunidades/${op.slug}`}
                        className="flex items-center justify-between gap-4 bg-white rounded-xl shadow p-4 hover:shadow-lg transition"
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-bold">
                              {TYPE_LABELS[op.type]}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${RISK_LABELS[op.riskLevel].className}`}>
                              {RISK_LABELS[op.riskLevel].label}
                            </span>
                          </div>
                          <p className="font-semibold text-gray-900 text-sm truncate">{op.name}</p>
                          <p className="text-gray-500 text-xs truncate">{op.description}</p>
                        </div>
                        <span className="text-blue-600 flex-shrink-0">→</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Atalhos: ferramentas e educação */}
              {isDefaultView && (
                <div className="mt-10">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Ferramentas e recursos</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {SHORTCUTS.map((shortcut) => (
                      <Link
                        key={shortcut.to}
                        to={shortcut.to}
                        className="flex items-center gap-3 bg-white p-4 rounded-xl shadow hover:shadow-lg transition"
                      >
                        <span className="text-2xl">{shortcut.icon}</span>
                        <div>
                          <div className="font-semibold text-gray-900 text-sm">{shortcut.title}</div>
                          <div className="text-gray-500 text-xs">{shortcut.desc}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <FearGreedWidget />
              <TopMovers />

              <div className="bg-white rounded-lg shadow p-4">
                <AdSense adSlot="9876543210" />
              </div>

              {/* Trending */}
              {trendingArticles.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-xl font-bold mb-4">🔥 Em Alta</h3>
                  <div className="space-y-4">
                    {trendingArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.slug}`}
                        className="block hover:bg-gray-50 p-2 rounded transition"
                      >
                        <h4 className="font-semibold text-sm line-clamp-2 mb-1">
                          {article.titlePt || article.title}
                        </h4>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{article.sourceName}</span>
                          {article.views > 0 && <span>{article.views} visualizações</span>}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Categorias */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-xl font-bold mb-4">📂 Categorias</h3>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <Link
                      key={category.id}
                      to={`/category/${category.slug}`}
                      className="flex items-center justify-between p-2 hover:bg-gray-50 rounded transition"
                    >
                      <span className="text-sm font-medium">{category.name}</span>
                      {category._count && (
                        <span className="text-xs text-gray-500">{category._count.articles}</span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
